import dayjs from 'dayjs'
import { Language } from '../../../../models/graphqlTypes'
import { formatQueryString } from '../../../../utils/formatQueryString'
import {
  Appointment,
  Survey,
  TreatmentPanelFilter,
  Referral,
} from '../types/treatments.types'

export type TreatmentItem = Appointment | Survey | Referral

export type TreatmentItemWithDate = TreatmentItem & {
  date: Date
}

export type TreatmentItemWithExtraData = TreatmentItem & {
  date: Date
  isPast: boolean
}

export interface TreatmentItems {
  appointments: TreatmentItemWithExtraData[]
  surveys: TreatmentItemWithExtraData[]
  referrals: TreatmentItemWithExtraData[]
}

export interface GroupedTreatmentItem {
  hasPast: boolean
  groups: { [key: string]: TreatmentItems }
}

const shouldIncludeItem = (
  searchString: string,
  filterValue: TreatmentPanelFilter
): boolean => {
  const filterArray =
    filterValue.searchTerm?.toLowerCase().replace('-', '').split(' ') ?? []

  return (
    !filterValue.searchTerm?.length ||
    filterArray.length ===
      filterArray.filter((filterword) =>
        formatQueryString(searchString).includes(filterword)
      ).length
  )
}

export const getFilteredAppointments = (
  appointments: Appointment[],
  selectedLanguage: Language,
  filterValue: TreatmentPanelFilter
): Appointment[] => {
  if (!filterValue.isAppointment) {
    return []
  }

  const dateSortedOrderedAppointments = [...appointments].sort((a, b) =>
    a.proposedDate > b.proposedDate ? 1 : -1
  )

  return dateSortedOrderedAppointments.filter((appointment) => {
    const searchString = [
      appointment.info?.doctorTitle[selectedLanguage],
      appointment.info?.doctorTodo?.[selectedLanguage],
      appointment.info?.goodToKnow?.[selectedLanguage],
      appointment.institution?.name,
      appointment.institution?.concatenatedAddress,
      appointment.doctor.title,
      appointment.doctor.firstName,
      appointment.doctor.lastName,
    ].join('_')

    return shouldIncludeItem(searchString, filterValue)
  })
}

export const getFilteredSurveys = (
  surveys: Survey[],
  filterValue: TreatmentPanelFilter
): Survey[] => {
  if (!filterValue.isSurvey) {
    return []
  }

  const dateSortedOrderedSurveys = [...surveys].sort((a, b) =>
    a.fillableFrom > b.fillableFrom ? 1 : -1
  )

  return dateSortedOrderedSurveys.filter((survey) => {
    const searchString = [
      survey.surveySchema.title,
      survey.surveySchema.description,
      survey.doctor.title,
      survey.doctor.firstName,
      survey.doctor.lastName,
    ].join('_')

    return shouldIncludeItem(searchString, filterValue)
  })
}

export const getFilteredReferrals = (
  referrals: Referral[],
  filterValue: TreatmentPanelFilter
): Referral[] => {
  if (!filterValue.isReferral) {
    return []
  }
  const dateSortedOrderedReferrals = [...referrals].sort((a, b) =>
    a.expirationDate > b.expirationDate ? 1 : -1
  )

  return dateSortedOrderedReferrals.filter((referral) => {
    const searchString = [
      referral.name,
      referral.description,
      referral.doctor.title,
      referral.doctor.firstName,
      referral.doctor.lastName,
    ].join('_')

    return shouldIncludeItem(searchString, filterValue)
  })
}

export const getGroupedItems = (
  appointments: Appointment[],
  surveys: Survey[],
  referrals: Referral[]
): GroupedTreatmentItem => {
  return [
    ...appointments,
    ...surveys,
    ...referrals,
  ].reduce<GroupedTreatmentItem>(
    (acc, item) => {
      const isAppointment = item.__typename === 'Appointment'
      const isSurvey = item.__typename === 'Survey'
      const isReferral = item.__typename === 'Referral'
      let date = null
      if (isAppointment) {
        date = item.proposedDate
      } else if (isSurvey) {
        date = item.fillableFrom
      } else if (isReferral) {
        date = item.expirationDate
      }
      const isPast = dayjs().startOf('day') > dayjs(date)
      acc.hasPast = acc.hasPast || isPast
      const year = dayjs(date).year()
      const month = dayjs(date).month()

      const groupKey = `${year}-${month}`
      const initialGroupItem: TreatmentItems = {
        appointments: [],
        surveys: [],
        referrals: [],
      }
      acc.groups[groupKey] = acc.groups[groupKey] || initialGroupItem
      const itemWithExtraData = { ...item, date, isPast }

      if (isAppointment) {
        acc.groups[groupKey].appointments.push(itemWithExtraData)
      } else if (isSurvey) {
        acc.groups[groupKey].surveys.push(itemWithExtraData)
      } else if (isReferral) {
        acc.groups[groupKey].referrals.push(itemWithExtraData)
      }
      return acc
    },
    { hasPast: false, groups: {} }
  )
}

export const isAppointment = (item: TreatmentItem): boolean =>
  item.__typename === 'Appointment'

export const sortAppointmentsAndSurveys = (
  appointments: Appointment[],
  surveys: Survey[]
): TreatmentItemWithDate[] =>
  [...appointments, ...surveys]
    .map((item) => {
      const date = isAppointment(item)
        ? (item as Appointment).proposedDate
        : (item as Survey).fillableFrom

      return { ...item, date }
    })
    .sort((a, b) => (a.date > b.date ? 1 : -1))
